// Bilingual translations for Raknati (EN + AR)
// Keep keys stable — used by all sections.

export const translations = {
  en: {
    nav: {
      home: "Home",
      how: "How It Works",
      features: "Features",
      solutions: "Solutions",
      partners: "For Partners",
      pricing: "Pricing",
      investors: "Investors",
      faq: "FAQ",
      contact: "Contact",
      cta_early: "Join Early Access",
      cta_pilot: "Request a Pilot",
      lang: "العربية",
    },
    hero: {
      badge: "Pre-launch · Built for the UAE",
      title: "Never Lose Track of Your Parked Car Again",
      tagline: "Park. Save. Return.",
      subtitle:
        "Raknati is an AI-powered parking assistant that remembers exactly where you parked and guides you back using location data, parking photos, floor and zone recognition, reminders, compass guidance and smart navigation.",
      cta_primary: "Join Early Access",
      cta_secondary: "Request a Business Pilot",
      trust: "Built for the UAE. Designed to scale across the GCC.",
      mock: {
        parked: "Vehicle parked",
        level: "Level P3",
        zone: "Zone F",
        bay: "Bay F-318",
        walk: "138 m · 2 min walk",
        find: "Find My Car",
        saved: "Saved 14:32 · Expires 18:00",
      },
    },
    problem: {
      eyebrow: "The Problem",
      title: "Finding a Parked Car Is Still Difficult Indoors",
      lede:
        "Drivers can remember the mall or building, but often forget the exact floor, zone, pillar, entrance or lift.",
      cards: [
        {
          t: "GPS Cannot Identify the Parking Floor",
          d: "A general pin can't tell you whether the car is on P1, P2 or P3 — GPS simply doesn't work reliably in basement parking.",
        },
        {
          t: "Parking Details Are Easily Forgotten",
          d: "Bay numbers, pillar colours, lift labels, entrances and the route you took to enter the mall — small details, big frustration.",
        },
        {
          t: "Existing Apps Save Only a Basic Pin",
          d: "Most map apps drop a single marker. They don't capture floor, zone, bay, landmark photos or expiry time.",
        },
      ],
      quote:
        "The problem is not finding the building. The problem is finding the exact parking spot inside it.",
    },
    solution: {
      eyebrow: "The Raknati Solution",
      title: "A Complete Parking Memory Assistant",
      lede:
        "Raknati saves the full parking context instead of recording only GPS coordinates.",
      features: [
        "Exact parking location",
        "Floor, zone and bay recording",
        "Parking photos",
        "Nearby landmark recording",
        "Voice notes",
        "Text notes",
        "Automatic parking detection",
        "AI parking-sign recognition",
        "Parking timer",
        "Expiry reminders",
        "Compass guidance",
        "Walking directions",
        "Offline parking access",
        "Multiple vehicles",
        "Family sharing",
        "Arabic and English support",
      ],
    },
    how: {
      eyebrow: "How It Works",
      title: "Three Simple Steps. One Stress-Free Return.",
      steps: [
        {
          n: "01",
          t: "Park My Car",
          d: "Tap Park My Car — or let Raknati detect that your Bluetooth, CarPlay or Android Auto connection has ended.",
        },
        {
          n: "02",
          t: "Save the Parking Context",
          d: "Raknati saves the location, floor, zone, bay, parking photos, notes, compass direction, date, time and expiry details.",
        },
        {
          n: "03",
          t: "Return to the Vehicle",
          d: "Tap Find My Car and follow the map, compass, photos and walking directions back to your exact spot.",
        },
      ],
    },
    smart: {
      eyebrow: "Smart Features",
      title: "Smarter Than a Parking Pin",
      lede:
        "A layered assistant that combines mobile sensors, AI and thoughtful design. Some capabilities are live in the beta, others are on our roadmap.",
      labels: { now: "Beta", planned: "Planned", soon: "Coming Soon", future: "Future Integration" },
      items: [
        { t: "Automatic parking detection", tag: "now" },
        { t: "Vehicle Bluetooth recognition", tag: "now" },
        { t: "Apple CarPlay support", tag: "soon" },
        { t: "Android Auto support", tag: "soon" },
        { t: "AI parking-zone recognition", tag: "planned" },
        { t: "AI bay-number recognition", tag: "planned" },
        { t: "Parking-sign photo scanning", tag: "planned" },
        { t: "Parking expiry alerts", tag: "now" },
        { t: "Payment reminders", tag: "soon" },
        { t: "Voice command: \"Where is my car?\"", tag: "planned" },
        { t: "Apple Watch support", tag: "soon" },
        { t: "Wear OS support", tag: "soon" },
        { t: "Multiple vehicle management", tag: "now" },
        { t: "Family vehicle sharing", tag: "soon" },
        { t: "Emergency location sharing", tag: "planned" },
        { t: "Offline parking records", tag: "now" },
        { t: "Compass navigation", tag: "now" },
        { t: "AR directional guidance", tag: "future" },
        { t: "Indoor navigation where supported", tag: "future" },
        { t: "Bluetooth beacon readiness", tag: "future" },
        { t: "UWB precision-location readiness", tag: "future" },
        { t: "EV charging-station locator", tag: "planned" },
        { t: "Parking availability prediction", tag: "future" },
        { t: "Smart-parking infrastructure integration", tag: "future" },
      ],
    },
    uae: {
      eyebrow: "Designed for the UAE",
      title: "Built for the Places UAE Drivers Visit Every Day",
      lede:
        "From basement parking in major malls to airport long-stay areas, Raknati is designed for the complex parking environments found across Dubai, Abu Dhabi, Sharjah and the wider UAE.",
      cases: [
        "Shopping malls",
        "Airports",
        "Hotels & resorts",
        "Hospitals",
        "Business districts",
        "Corporate campuses",
        "Exhibition centres",
        "Residential communities",
        "Public parking",
        "Rental-car parking",
        "Valet parking",
        "Tourist attractions",
      ],
    },
    compare: {
      eyebrow: "What Makes Raknati Different",
      title: "More Than \"Save My Parking Location\"",
      lede:
        "Raknati bridges the gap between a basic parking marker and infrastructure-heavy smart-parking platforms.",
      basic_title: "Basic Map Apps",
      raknati_title: "Raknati",
      basic: [
        "General GPS location only",
        "Basic outdoor directions",
        "Limited indoor parking info",
        "No floor or bay recognition",
        "No venue-specific platform",
      ],
      raknati: [
        "Floor, zone, bay and landmarks",
        "AI-based parking-sign reading",
        "Parking photos & visual clues",
        "Works offline in basements",
        "Compass and AR guidance",
        "Parking expiry reminders",
        "Full Arabic & English support",
        "Multi-vehicle & family sharing",
        "Venue & operator integrations",
        "Beacon / UWB-ready architecture",
        "Business dashboards & APIs",
      ],
    },
    business: {
      eyebrow: "For Businesses",
      title: "A Better Parking Experience for Your Visitors",
      lede:
        "Give visitors, guests, patients and employees a stress-free return to their vehicle — while unlocking operational insight for your team.",
      solutions: [
        { t: "Shopping malls", d: "Help visitors remember floors, zones, entrances, lifts and parking bays." },
        { t: "Airports", d: "Support travellers, tourists and rental-car users in large long-stay facilities." },
        { t: "Hotels & resorts", d: "Improve guest, event and valet parking experiences." },
        { t: "Hospitals", d: "Help patients, visitors and elderly drivers return to their vehicles more easily." },
        { t: "Corporate campuses", d: "Support employees, visitors and fleet vehicles across your sites." },
        { t: "Parking operators", d: "Integrate parking maps, QR markers, expiry reminders and navigation." },
        { t: "Car-rental companies", d: "Help customers locate rental vehicles and return them to designated areas." },
        { t: "Valet operators", d: "Digital vehicle handover and collection guidance for smoother service." },
      ],
      benefits_title: "Business Benefits",
      benefits: [
        "Improve visitor satisfaction",
        "Reduce customer frustration",
        "Reduce staff time locating vehicles",
        "Digital parking maps",
        "QR location markers",
        "Accessible walking routes",
        "Parking expiry alerts",
        "Valet & parking-system integration",
        "Anonymised operational insights",
        "Smart-building positioning",
        "White-label or co-branded options",
      ],
      cta: "Request a Venue Pilot",
      form: {
        title: "Business Enquiry",
        subtitle: "Tell us about your venue — we'll be in touch within 2 business days.",
        full_name: "Full name",
        organisation: "Organisation",
        job_title: "Job title",
        email: "Business email",
        mobile: "Mobile number",
        organisation_type: "Organisation type",
        organisation_type_ph: "Select organisation type",
        organisation_types: [
          "Shopping mall", "Airport", "Hotel or resort", "Hospital",
          "University", "Corporate campus", "Exhibition centre",
          "Property developer", "Parking operator", "Municipality",
          "Car-rental company", "Valet operator", "Fleet company", "Other",
        ],
        emirate: "Emirate",
        facilities: "Number of parking facilities",
        pilot_location: "Proposed pilot location",
        message: "Message",
        submit: "Send Enquiry",
        success: "Thank you. Our partnerships team will contact you shortly.",
        error: "Something went wrong. Please try again.",
      },
    },
    pricing: {
      eyebrow: "Pricing & Business Model",
      title: "Flexible Plans for Drivers, Families and Businesses",
      lede: "Indicative pre-launch pricing. Final pricing may change before commercial launch.",
      plans: [
        {
          name: "Free",
          price: "AED 0",
          period: "forever",
          features: [
            "One vehicle",
            "Manual parking save",
            "GPS location",
            "Parking photos",
            "Text notes",
            "Basic timer",
            "Walking directions",
            "Limited parking history",
          ],
          cta: "Join Early Access",
          highlight: false,
        },
        {
          name: "Premium",
          price: "AED 5.99",
          period: "per month · AED 49.99 / year",
          features: [
            "Automatic parking detection",
            "AI parking-sign recognition",
            "Multiple photographs",
            "Cloud synchronisation",
            "Extended parking history",
            "Multiple vehicles",
            "Advanced reminders",
            "Voice shortcuts",
            "Advanced navigation",
          ],
          cta: "Join Early Access",
          highlight: true,
          badge: "Proposed Pre-Launch Pricing",
        },
        {
          name: "Family",
          price: "Coming Soon",
          period: "up to 5 members",
          features: [
            "Up to five family members",
            "Shared vehicles",
            "Parking-location sharing",
            "Emergency sharing",
            "Shared reminders",
            "Multiple vehicle support",
          ],
          cta: "Register Interest",
          highlight: false,
        },
        {
          name: "Business & Venue",
          price: "Custom",
          period: "tailored pricing",
          features: [
            "Fleet accounts",
            "Partner dashboard",
            "Venue maps",
            "QR markers",
            "Parking APIs",
            "Analytics",
            "White-label options",
            "Custom integrations",
          ],
          cta: "Request Custom Pricing",
          highlight: false,
        },
      ],
      note: "Online payment is not activated during the pre-launch stage.",
    },
    investors: {
      eyebrow: "For Investors",
      title: "Building the Smart Parking Memory Layer for the GCC",
      lede:
        "Raknati addresses a clear, frequent consumer pain point across the UAE's most-visited destinations — with a scalable path into venue licensing, fleet and API revenue.",
      thesis: [
        "Clear and frequent consumer problem",
        "Strong relevance to UAE malls, airports and large venues",
        "Scalable consumer and business model",
        "Multiple revenue streams: subscriptions, family plans, fleet, venue licensing and APIs",
        "Expansion potential across GCC markets",
        "Aligned with smart mobility, tourism and digital transformation",
        "Growing venue-partner network opportunity",
        "Potential to build valuable parking-location infrastructure",
      ],
      roadmap_title: "Development Roadmap",
      phases: [
        { p: "Phase 1", t: "UAE MVP & beta validation" },
        { p: "Phase 2", t: "AI-powered recognition & smart features" },
        { p: "Phase 3", t: "Venue pilots & indoor parking maps" },
        { p: "Phase 4", t: "Smart-parking & payment integrations" },
        { p: "Phase 5", t: "GCC expansion" },
      ],
      cta_deck: "Request Investor Deck",
      cta_explore: "Explore Investment Opportunity",
      cta_meet: "Schedule a Meeting",
      form: {
        title: "Investor Enquiry",
        subtitle: "Share a few details — our founding team will follow up personally.",
        full_name: "Full name",
        company: "Company or investment fund",
        investor_type: "Investor type",
        investor_type_ph: "Select investor type",
        investor_types: [
          "Angel investor", "Family office", "Venture capital",
          "Corporate venture", "Government / sovereign fund",
          "Strategic partner", "Other",
        ],
        email: "Email",
        mobile: "Mobile number",
        stage: "Preferred investment stage",
        stage_ph: "Select stage",
        stages: ["Pre-seed", "Seed", "Series A", "Later stage", "Undecided"],
        message: "Message",
        submit: "Send Enquiry",
        success: "Thank you. A member of our founding team will reach out.",
        note: "Confidential financial projections are shared privately after initial contact.",
      },
    },
    early: {
      eyebrow: "Early Access",
      title: "Be Among the First to Experience Raknati",
      lede:
        "Join the UAE beta community and help shape the future of smart parking navigation.",
      form: {
        full_name: "Full name",
        email: "Email",
        mobile: "Mobile number",
        emirate: "Emirate",
        emirate_ph: "Select emirate",
        emirates: ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"],
        device: "Device type",
        devices: ["iPhone", "Android"],
        use_case: "Primary use case",
        use_case_ph: "e.g. mall parking, airport, corporate",
        venue: "Frequently visited mall or venue",
        beta_interest: "I'm interested in beta testing",
        consent:
          "I agree to be contacted by Raknati regarding beta access and product updates.",
        submit: "Join the Waitlist",
        success:
          "Thank you for joining Raknati. Selected beta users will be contacted when testing begins.",
      },
    },
    faq: {
      eyebrow: "FAQ",
      title: "Frequently Asked Questions",
      items: [
        {
          q: "What is Raknati?",
          a: "Raknati is an AI-powered smart parking assistant that saves the complete context of where you parked — floor, zone, bay, photos, landmarks, timer — and helps guide you back to your vehicle.",
        },
        {
          q: "How does Raknati remember where I parked?",
          a: "By combining GPS coordinates, floor and zone data you record, parking photographs, notes, compass direction and — where supported — indoor navigation signals.",
        },
        {
          q: "Does Raknati work in basement parking?",
          a: "Yes. Because Raknati doesn't rely on GPS alone, it uses your saved photos, floor, zone, bay, notes and compass guidance to help you return to your car even underground.",
        },
        {
          q: "Does Raknati require internet access?",
          a: "Core parking records work offline. Cloud sync, AI recognition and advanced features require an internet connection.",
        },
        {
          q: "Can Raknati automatically detect when I park?",
          a: "Yes. When your vehicle's Bluetooth, CarPlay or Android Auto connection ends, Raknati can automatically save your parking location.",
        },
        {
          q: "Can Raknati identify the parking floor and bay?",
          a: "Raknati supports manual recording of floor, zone and bay today. AI-based sign recognition is on our roadmap.",
        },
        {
          q: "Will Raknati be available on iPhone and Android?",
          a: "Yes. Raknati is being built for iPhone and Android from day one.",
        },
        {
          q: "Does Raknati support Arabic?",
          a: "Yes. Raknati fully supports Arabic and English, including right-to-left layout.",
        },
        {
          q: "Can families share vehicle locations?",
          a: "Family sharing and emergency location sharing are planned features on the Family plan.",
        },
        {
          q: "Can malls, airports and hotels partner with Raknati?",
          a: "Yes. Venue pilots, dashboards, QR markers and APIs are available. Please use the Business Enquiry form to start a conversation.",
        },
        {
          q: "Is Raknati currently available?",
          a: "Raknati is currently in the pre-launch stage. Join the waitlist to be notified when beta testing begins.",
        },
        {
          q: "How can businesses or investors contact Raknati?",
          a: "Use the Business Enquiry or Investor Enquiry forms on this page — or write to us via the general Contact section.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's Build the Future of Parking Together",
      lede: "Tell us how we can help. We reply to every message.",
      categories: [
        "Early Access",
        "Venue Partnership",
        "Business Enquiry",
        "Investor Enquiry",
        "Technology Partnership",
        "General Enquiry",
      ],
      form: {
        full_name: "Full name",
        email: "Email",
        mobile: "Mobile number",
        enquiry_type: "Enquiry type",
        enquiry_type_ph: "Select enquiry type",
        company: "Company (if applicable)",
        emirate: "Emirate",
        message: "Message",
        submit: "Send Message",
        success: "Message received. We'll respond shortly.",
      },
      location: "United Arab Emirates",
      social: "Follow Raknati",
    },
    footer: {
      tagline: "Park. Save. Return.",
      product: "Product",
      company: "Company",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      cookies: "Cookie Policy",
      data_deletion: "Data Deletion Request",
      disclaimer:
        "Raknati is currently under development. Features, pricing, availability and integrations may change before commercial launch. References to venues, cities or technology platforms do not imply an official partnership unless explicitly confirmed.",
      rights: "All rights reserved.",
    },
    common: {
      submitting: "Submitting…",
      required: "Required",
      email_invalid: "Please enter a valid email address.",
      whatsapp: "Chat on WhatsApp",
    },
    privacy: {
      title: "Privacy Policy",
      updated: "Draft · pre-launch",
      intro:
        "This draft outlines how Raknati handles data collected via this website and, in the future, the Raknati mobile application. Final privacy and legal documents will be reviewed by qualified UAE legal counsel prior to commercial launch.",
      sections: [
        { t: "Location data collection", d: "Raknati collects precise and approximate location data to help you save and return to your parked vehicle. Location is only accessed with your consent." },
        { t: "Background location permission", d: "Automatic parking detection uses background location. You may disable this in your device settings at any time." },
        { t: "Bluetooth usage", d: "Bluetooth is used to detect when your vehicle connection ends so we can auto-save your parking location. We do not share Bluetooth device data with third parties." },
        { t: "Parking photographs", d: "Photos you take within Raknati are stored on your device by default and optionally synced to your Raknati account." },
        { t: "Voice and text notes", d: "Voice and text notes are stored securely and associated with the parking record you create." },
        { t: "Account information", d: "Name, email and mobile number are used to create your account and communicate about the service." },
        { t: "Cloud storage", d: "Cloud sync is optional and encrypted in transit. You can request deletion at any time." },
        { t: "Analytics", d: "We collect anonymised product analytics to improve Raknati. We do not sell personal data." },
        { t: "Data retention", d: "You control your parking history and may delete records or your account at any time." },
        { t: "Data access, correction & deletion", d: "You may request access to, correction of, or deletion of your personal data via Info@raknati.com." },
        { t: "Family sharing", d: "If you enable family sharing, selected data is shared with the family members you invite." },
        { t: "Third-party service providers", d: "We use trusted cloud, communication and analytics providers. Each is bound by data-protection obligations." },
        { t: "Security controls", d: "We use industry-standard encryption, access controls and monitoring to protect your data." },
        { t: "Consent management", d: "You may withdraw consent for any optional data processing at any time." },
        { t: "Contact", d: "Questions? Write to Info@raknati.com." },
      ],
      disclaimer:
        "This document is a working draft and must be reviewed by a qualified UAE legal professional before launch.",
    },
  },

  ar: {
    nav: {
      home: "الرئيسية",
      how: "كيف تعمل",
      features: "الميزات",
      solutions: "الحلول",
      partners: "للشركاء",
      pricing: "الأسعار",
      investors: "المستثمرون",
      faq: "الأسئلة الشائعة",
      contact: "تواصل",
      cta_early: "انضم للوصول المبكر",
      cta_pilot: "اطلب تجربة تجارية",
      lang: "English",
    },
    hero: {
      badge: "قبل الإطلاق · مصمم للإمارات",
      title: "لن تنسَ مكان سيارتك بعد اليوم",
      tagline: "اركن. احفظ. ارجع.",
      subtitle:
        "ركنتي مساعد ركن مدعوم بالذكاء الاصطناعي، يتذكر بالضبط أين ركنت سيارتك ويرشدك للعودة إليها عبر الموقع، صور الركن، التعرّف على الطابق والمنطقة، التنبيهات، البوصلة والملاحة الذكية.",
      cta_primary: "انضم للوصول المبكر",
      cta_secondary: "اطلب تجربة تجارية",
      trust: "مصمّم للإمارات. جاهز للتوسّع في دول الخليج.",
      mock: {
        parked: "تم ركن السيارة",
        level: "الطابق P3",
        zone: "المنطقة F",
        bay: "الموقف F-318",
        walk: "138 م · 2 دقيقة سيراً",
        find: "ابحث عن سيارتي",
        saved: "تم الحفظ 14:32 · تنتهي 18:00",
      },
    },
    problem: {
      eyebrow: "المشكلة",
      title: "العثور على السيارة في المواقف الداخلية لا يزال صعباً",
      lede:
        "يتذكر السائقون المول أو المبنى، لكن ينسون الطابق أو المنطقة أو العمود أو المدخل أو المصعد.",
      cards: [
        {
          t: "GPS لا يحدد طابق الركن",
          d: "قد يعرض GPS موقعاً عاماً، لكنه لا يستطيع تحديد ما إذا كنت في P1 أو P2 أو P3 في مواقف تحت الأرض.",
        },
        {
          t: "تفاصيل الركن تُنسى بسهولة",
          d: "أرقام المواقف، ألوان الأعمدة، أرقام المصاعد، المدخل الذي دخلت منه — تفاصيل صغيرة لكنها مزعجة.",
        },
        {
          t: "التطبيقات الحالية تحفظ دبوساً فقط",
          d: "معظم تطبيقات الخرائط تحفظ علامة موقع عامة فقط، دون سياق الركن الداخلي الكامل.",
        },
      ],
      quote:
        "المشكلة ليست في إيجاد المبنى. المشكلة في إيجاد المكان الدقيق داخل الموقف.",
    },
    solution: {
      eyebrow: "حل ركنتي",
      title: "مساعد كامل لتذكّر مواقف السيارة",
      lede:
        "ركنتي يحفظ سياق الركن الكامل، وليس فقط إحداثيات GPS.",
      features: [
        "الموقع الدقيق للركن",
        "تسجيل الطابق والمنطقة والموقف",
        "صور الركن",
        "تسجيل المعالم القريبة",
        "ملاحظات صوتية",
        "ملاحظات نصية",
        "كشف الركن التلقائي",
        "تعرّف ذكي على لوحات الركن",
        "مؤقّت الركن",
        "تنبيهات انتهاء المدة",
        "التوجيه بالبوصلة",
        "توجيهات المشي",
        "الوصول دون إنترنت",
        "سيارات متعددة",
        "المشاركة العائلية",
        "دعم العربية والإنجليزية",
      ],
    },
    how: {
      eyebrow: "كيف تعمل",
      title: "ثلاث خطوات بسيطة. عودة بلا توتر.",
      steps: [
        {
          n: "01",
          t: "اركن سيارتي",
          d: "اضغط \"اركن سيارتي\" — أو دع ركنتي يكتشف انتهاء اتصال البلوتوث أو CarPlay أو Android Auto.",
        },
        {
          n: "02",
          t: "احفظ سياق الركن",
          d: "يحفظ ركنتي الموقع والطابق والمنطقة والموقف والصور والملاحظات واتجاه البوصلة والوقت وموعد الانتهاء.",
        },
        {
          n: "03",
          t: "عُد إلى السيارة",
          d: "اضغط \"ابحث عن سيارتي\" واتبع الخريطة والبوصلة والصور والتوجيهات للوصول إلى مكانك تماماً.",
        },
      ],
    },
    smart: {
      eyebrow: "ميزات ذكية",
      title: "أذكى من مجرد دبوس ركن",
      lede:
        "مساعد متكامل يجمع بين المستشعرات والذكاء الاصطناعي والتصميم المدروس. بعض الميزات متوفرة في النسخة التجريبية، وأخرى ضمن خريطة الطريق.",
      labels: { now: "تجريبي", planned: "مخطط", soon: "قريباً", future: "تكامل مستقبلي" },
      items: [
        { t: "كشف الركن التلقائي", tag: "now" },
        { t: "التعرّف عبر بلوتوث السيارة", tag: "now" },
        { t: "دعم Apple CarPlay", tag: "soon" },
        { t: "دعم Android Auto", tag: "soon" },
        { t: "التعرّف الذكي على منطقة الركن", tag: "planned" },
        { t: "التعرّف الذكي على رقم الموقف", tag: "planned" },
        { t: "مسح لوحات الركن بالصور", tag: "planned" },
        { t: "تنبيهات انتهاء الركن", tag: "now" },
        { t: "تذكير الدفع", tag: "soon" },
        { t: "أمر صوتي: \"أين سيارتي؟\"", tag: "planned" },
        { t: "دعم Apple Watch", tag: "soon" },
        { t: "دعم Wear OS", tag: "soon" },
        { t: "إدارة سيارات متعددة", tag: "now" },
        { t: "مشاركة السيارة عائلياً", tag: "soon" },
        { t: "مشاركة الموقع للطوارئ", tag: "planned" },
        { t: "سجلات الركن دون إنترنت", tag: "now" },
        { t: "الملاحة بالبوصلة", tag: "now" },
        { t: "توجيه بالواقع المعزز", tag: "future" },
        { t: "ملاحة داخلية حيثما تدعم", tag: "future" },
        { t: "جاهزية لأجهزة البلوتوث Beacon", tag: "future" },
        { t: "جاهزية لدقة UWB", tag: "future" },
        { t: "محدد مواقع شحن السيارات الكهربائية", tag: "planned" },
        { t: "توقّع توفر المواقف", tag: "future" },
        { t: "تكامل مع البنية التحتية الذكية", tag: "future" },
      ],
    },
    uae: {
      eyebrow: "مصمم للإمارات",
      title: "لأماكن سائقي الإمارات التي يزورونها كل يوم",
      lede:
        "من مواقف الطوابق السفلية في أكبر المولات إلى مواقف المطارات الطويلة، صُمّم ركنتي لبيئات الركن المعقدة في دبي وأبوظبي والشارقة وجميع الإمارات.",
      cases: [
        "مراكز التسوق",
        "المطارات",
        "الفنادق والمنتجعات",
        "المستشفيات",
        "المناطق التجارية",
        "المجمعات المؤسسية",
        "مراكز المعارض",
        "المجمعات السكنية",
        "المواقف العامة",
        "مواقف تأجير السيارات",
        "مواقف الفاليه",
        "المعالم السياحية",
      ],
    },
    compare: {
      eyebrow: "ما يميز ركنتي",
      title: "أكثر من مجرد \"احفظ موقع الركن\"",
      lede:
        "ركنتي يربط بين الدبوس البسيط ومنصات المواقف الذكية الكبيرة.",
      basic_title: "تطبيقات الخرائط العادية",
      raknati_title: "ركنتي",
      basic: [
        "موقع GPS عام فقط",
        "توجيهات خارجية بسيطة",
        "معلومات مواقف داخلية محدودة",
        "لا يوجد تمييز للطابق أو الموقف",
        "لا توجد منصة خاصة بالمنشأة",
      ],
      raknati: [
        "الطابق والمنطقة والموقف والمعالم",
        "قراءة لوحات الركن بالذكاء الاصطناعي",
        "صور وملاحظات بصرية",
        "يعمل دون إنترنت في الأقبية",
        "توجيه بالبوصلة والواقع المعزز",
        "تذكير بانتهاء الركن",
        "دعم كامل للعربية والإنجليزية",
        "سيارات متعددة ومشاركة عائلية",
        "تكاملات مع المنشآت والمشغلين",
        "معمارية جاهزة لـ Beacon و UWB",
        "لوحات وأدوات API للأعمال",
      ],
    },
    business: {
      eyebrow: "للأعمال",
      title: "تجربة ركن أفضل لزوّاركم",
      lede:
        "امنح الزوّار والضيوف والمرضى والموظفين عودة سهلة إلى سياراتهم — مع رؤى تشغيلية لفريقك.",
      solutions: [
        { t: "مراكز التسوق", d: "ساعد الزوار على تذكّر الطوابق والمناطق والمداخل والمصاعد ومواقف السيارات." },
        { t: "المطارات", d: "دعم المسافرين والسياح وعملاء تأجير السيارات في المواقف الكبيرة والطويلة." },
        { t: "الفنادق والمنتجعات", d: "تحسين تجربة الضيوف والفعاليات والفاليه." },
        { t: "المستشفيات", d: "ساعد المرضى والزوار وكبار السن على العودة إلى سياراتهم بسهولة." },
        { t: "المجمعات المؤسسية", d: "دعم الموظفين والزوار والمركبات في جميع المواقع." },
        { t: "مشغلو المواقف", d: "دمج خرائط المواقف ورموز QR والتنبيهات والملاحة." },
        { t: "شركات تأجير السيارات", d: "ساعد العملاء في إيجاد سيارات الإيجار وإعادتها." },
        { t: "مشغلو الفاليه", d: "تسليم واستلام رقمي للمركبات لخدمة أسرع." },
      ],
      benefits_title: "فوائد للأعمال",
      benefits: [
        "تحسين رضا الزوار",
        "تقليل إحباط العملاء",
        "توفير وقت الموظفين",
        "خرائط مواقف رقمية",
        "علامات QR للمواقع",
        "مسارات مشي ميسّرة",
        "تنبيهات انتهاء الركن",
        "تكامل مع أنظمة الفاليه",
        "رؤى تشغيلية مجهولة الهوية",
        "تعزيز مكانة المبنى الذكي",
        "خيارات علامة بيضاء أو مشتركة",
      ],
      cta: "اطلب تجربة للمنشأة",
      form: {
        title: "استفسار الأعمال",
        subtitle: "أخبرنا عن منشأتكم — سنتواصل خلال يومي عمل.",
        full_name: "الاسم الكامل",
        organisation: "المؤسسة",
        job_title: "المسمى الوظيفي",
        email: "البريد الإلكتروني",
        mobile: "رقم الجوال",
        organisation_type: "نوع المؤسسة",
        organisation_type_ph: "اختر نوع المؤسسة",
        organisation_types: [
          "مركز تسوق", "مطار", "فندق أو منتجع", "مستشفى",
          "جامعة", "مجمع مؤسسي", "مركز معارض",
          "مطوّر عقاري", "مشغل مواقف", "بلدية",
          "شركة تأجير سيارات", "مشغل فاليه", "شركة أسطول", "أخرى",
        ],
        emirate: "الإمارة",
        facilities: "عدد مرافق الركن",
        pilot_location: "الموقع المقترح للتجربة",
        message: "رسالة",
        submit: "أرسل الاستفسار",
        success: "شكراً لك. سيتواصل فريق الشراكات معك قريباً.",
        error: "حدث خطأ. يرجى المحاولة مرة أخرى.",
      },
    },
    pricing: {
      eyebrow: "الأسعار ونموذج العمل",
      title: "خطط مرنة للسائقين والعائلات والشركات",
      lede: "أسعار مبدئية قبل الإطلاق. قد تتغيّر قبل الإطلاق التجاري.",
      plans: [
        {
          name: "مجاني",
          price: "0 درهم",
          period: "للأبد",
          features: [
            "سيارة واحدة",
            "حفظ الركن يدوياً",
            "موقع GPS",
            "صور الركن",
            "ملاحظات نصية",
            "مؤقت أساسي",
            "توجيهات المشي",
            "سجل ركن محدود",
          ],
          cta: "انضم للوصول المبكر",
          highlight: false,
        },
        {
          name: "بريميوم",
          price: "5.99 د.إ",
          period: "شهرياً · 49.99 د.إ سنوياً",
          features: [
            "كشف الركن التلقائي",
            "التعرّف الذكي على لوحات الركن",
            "صور متعددة",
            "المزامنة السحابية",
            "سجل ركن موسع",
            "سيارات متعددة",
            "تنبيهات متقدمة",
            "اختصارات صوتية",
            "ملاحة متقدمة",
          ],
          cta: "انضم للوصول المبكر",
          highlight: true,
          badge: "سعر مقترح قبل الإطلاق",
        },
        {
          name: "العائلة",
          price: "قريباً",
          period: "حتى 5 أعضاء",
          features: [
            "حتى 5 أفراد من العائلة",
            "سيارات مشتركة",
            "مشاركة موقع الركن",
            "مشاركة الطوارئ",
            "تنبيهات مشتركة",
            "دعم عدة سيارات",
          ],
          cta: "سجّل اهتمامك",
          highlight: false,
        },
        {
          name: "الأعمال والمنشآت",
          price: "مخصّص",
          period: "أسعار حسب الطلب",
          features: [
            "حسابات أسطول",
            "لوحة شريك",
            "خرائط المنشأة",
            "علامات QR",
            "APIs للمواقف",
            "تحليلات",
            "خيارات علامة بيضاء",
            "تكاملات مخصصة",
          ],
          cta: "اطلب سعراً مخصصاً",
          highlight: false,
        },
      ],
      note: "لم يتم تفعيل الدفع الإلكتروني في مرحلة ما قبل الإطلاق.",
    },
    investors: {
      eyebrow: "للمستثمرين",
      title: "نبني طبقة ذاكرة الركن الذكية للخليج",
      lede:
        "يعالج ركنتي مشكلة يومية واضحة عبر الوجهات الأكثر زيارة في الإمارات — مع مسار قابل للتوسع في تراخيص المنشآت والأساطيل و APIs.",
      thesis: [
        "مشكلة استهلاكية واضحة ومتكررة",
        "أهمية عالية لمولات ومطارات ومنشآت الإمارات",
        "نموذج قابل للتوسع للأفراد والشركات",
        "مصادر دخل متعددة: اشتراكات، عائلات، أساطيل، تراخيص منشآت، APIs",
        "إمكانية توسّع في دول الخليج",
        "متوافق مع التنقل الذكي والسياحة والتحول الرقمي",
        "فرصة لبناء شبكة شركاء منشآت",
        "إمكانية بناء بنية تحتية قيّمة لمواقع الركن",
      ],
      roadmap_title: "خارطة الطريق",
      phases: [
        { p: "المرحلة 1", t: "MVP الإمارات وتحقق تجريبي" },
        { p: "المرحلة 2", t: "تعرّف مدعوم بالذكاء وميزات ذكية" },
        { p: "المرحلة 3", t: "تجارب المنشآت وخرائط داخلية" },
        { p: "المرحلة 4", t: "تكاملات مواقف ذكية ومدفوعات" },
        { p: "المرحلة 5", t: "التوسع في الخليج" },
      ],
      cta_deck: "اطلب عرض المستثمرين",
      cta_explore: "استكشف فرصة الاستثمار",
      cta_meet: "احجز لقاءً",
      form: {
        title: "استفسار المستثمرين",
        subtitle: "شارك بعض التفاصيل — سيتواصل الفريق المؤسس معك شخصياً.",
        full_name: "الاسم الكامل",
        company: "الشركة أو صندوق الاستثمار",
        investor_type: "نوع المستثمر",
        investor_type_ph: "اختر نوع المستثمر",
        investor_types: [
          "مستثمر ملاك", "مكتب عائلي", "رأس مال جريء",
          "استثمار مؤسسي", "صندوق حكومي / سيادي",
          "شريك استراتيجي", "أخرى",
        ],
        email: "البريد الإلكتروني",
        mobile: "رقم الجوال",
        stage: "مرحلة الاستثمار المفضلة",
        stage_ph: "اختر المرحلة",
        stages: ["ما قبل التأسيس", "التأسيس", "السلسلة A", "مرحلة متأخرة", "غير محدد"],
        message: "رسالة",
        submit: "أرسل الاستفسار",
        success: "شكراً لك. سيتواصل معك عضو من الفريق المؤسس.",
        note: "تُشارك التوقعات المالية السرية بعد التواصل المبدئي.",
      },
    },
    early: {
      eyebrow: "الوصول المبكر",
      title: "كن من أول من يجرب ركنتي",
      lede:
        "انضم إلى مجتمع المختبرين في الإمارات وساهم في تشكيل مستقبل ملاحة الركن الذكية.",
      form: {
        full_name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        mobile: "رقم الجوال",
        emirate: "الإمارة",
        emirate_ph: "اختر الإمارة",
        emirates: ["أبوظبي", "دبي", "الشارقة", "عجمان", "أم القيوين", "رأس الخيمة", "الفجيرة"],
        device: "نوع الجهاز",
        devices: ["iPhone", "Android"],
        use_case: "الاستخدام الأساسي",
        use_case_ph: "مثلاً: مواقف المولات أو المطارات",
        venue: "أكثر مكان تزوره",
        beta_interest: "أرغب في المشاركة بالنسخة التجريبية",
        consent:
          "أوافق على تواصل ركنتي معي بخصوص الوصول المبكر وتحديثات المنتج.",
        submit: "انضم لقائمة الانتظار",
        success:
          "شكراً لانضمامك إلى ركنتي. سنتواصل مع المستخدمين المختارين عند بدء الاختبار.",
      },
    },
    faq: {
      eyebrow: "الأسئلة الشائعة",
      title: "أسئلة يسألها الجميع",
      items: [
        { q: "ما هو ركنتي؟", a: "ركنتي مساعد ذكي للركن يحفظ سياق مكان الركن بالكامل — الطابق والمنطقة والموقف والصور والمعالم والمؤقت — ويرشدك للعودة إلى سيارتك." },
        { q: "كيف يتذكر ركنتي مكاني؟", a: "بدمج إحداثيات GPS والطابق والمنطقة والصور والملاحظات واتجاه البوصلة — وإشارات الملاحة الداخلية حيثما تُدعم." },
        { q: "هل يعمل ركنتي في مواقف الطوابق السفلية؟", a: "نعم. لأنه لا يعتمد على GPS فقط، بل يستخدم الصور والطابق والمنطقة والملاحظات والبوصلة." },
        { q: "هل يحتاج ركنتي إلى إنترنت؟", a: "الوظائف الأساسية تعمل دون إنترنت. المزامنة السحابية والذكاء الاصطناعي تحتاج اتصالاً." },
        { q: "هل يكشف ركنتي الركن تلقائياً؟", a: "نعم. عند انتهاء اتصال البلوتوث أو CarPlay أو Android Auto، يحفظ ركنتي موقع الركن." },
        { q: "هل يحدد الطابق والموقف؟", a: "يدعم ركنتي التسجيل اليدوي للطابق والمنطقة والموقف اليوم. التعرّف الذكي على اللوحات ضمن خارطة الطريق." },
        { q: "هل يتوفر على iPhone و Android؟", a: "نعم. ركنتي مصمم لكلا النظامين منذ البداية." },
        { q: "هل يدعم اللغة العربية؟", a: "نعم. ركنتي يدعم العربية والإنجليزية بالكامل مع تصميم من اليمين إلى اليسار." },
        { q: "هل يمكن للعائلات المشاركة؟", a: "المشاركة العائلية ومشاركة الطوارئ ضمن خطة العائلة." },
        { q: "هل يمكن للمولات والمطارات الشراكة؟", a: "نعم. تجارب المنشآت واللوحات وعلامات QR و APIs متاحة عبر نموذج استفسار الأعمال." },
        { q: "هل ركنتي متاح حالياً؟", a: "ركنتي في مرحلة ما قبل الإطلاق. انضم لقائمة الانتظار لتصلك التحديثات." },
        { q: "كيف نتواصل معكم؟", a: "استخدم نموذج استفسار الأعمال أو المستثمرين أو قسم التواصل العام." },
      ],
    },
    contact: {
      eyebrow: "تواصل",
      title: "لنبنِ مستقبل الركن معاً",
      lede: "أخبرنا كيف نساعدك — نرد على كل رسالة.",
      categories: [
        "الوصول المبكر",
        "شراكة منشأة",
        "استفسار أعمال",
        "استفسار مستثمر",
        "شراكة تقنية",
        "استفسار عام",
      ],
      form: {
        full_name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        mobile: "رقم الجوال",
        enquiry_type: "نوع الاستفسار",
        enquiry_type_ph: "اختر نوع الاستفسار",
        company: "الشركة (اختياري)",
        emirate: "الإمارة",
        message: "الرسالة",
        submit: "أرسل الرسالة",
        success: "تم استلام رسالتك. سنرد قريباً.",
      },
      location: "الإمارات العربية المتحدة",
      social: "تابع ركنتي",
    },
    footer: {
      tagline: "اركن. احفظ. ارجع.",
      product: "المنتج",
      company: "الشركة",
      legal: "قانوني",
      privacy: "سياسة الخصوصية",
      terms: "شروط الاستخدام",
      cookies: "سياسة الكوكيز",
      data_deletion: "طلب حذف البيانات",
      disclaimer:
        "ركنتي قيد التطوير حالياً. قد تتغيّر الميزات والأسعار والتوفر والتكاملات قبل الإطلاق. الإشارات إلى المنشآت أو المدن أو المنصات لا تعني شراكة رسمية إلا إذا تم تأكيدها.",
      rights: "جميع الحقوق محفوظة.",
    },
    common: {
      submitting: "جاري الإرسال…",
      required: "مطلوب",
      email_invalid: "يرجى إدخال بريد إلكتروني صحيح.",
      whatsapp: "تحدث عبر واتساب",
    },
    privacy: {
      title: "سياسة الخصوصية",
      updated: "مسودة · قبل الإطلاق",
      intro:
        "توضح هذه المسودة كيفية تعامل ركنتي مع البيانات المجمّعة عبر هذا الموقع، ومستقبلاً عبر تطبيق ركنتي. ستتم مراجعة الوثائق النهائية من قِبل مستشار قانوني إماراتي مؤهل قبل الإطلاق التجاري.",
      sections: [
        { t: "جمع بيانات الموقع", d: "يجمع ركنتي بيانات موقع دقيقة وتقريبية لمساعدتك على حفظ سيارتك والعودة إليها، وذلك بموافقتك فقط." },
        { t: "إذن الموقع في الخلفية", d: "يستخدم الكشف التلقائي الموقع في الخلفية. يمكنك تعطيله من إعدادات الجهاز." },
        { t: "استخدام البلوتوث", d: "يُستخدم البلوتوث للكشف عن انتهاء اتصال سيارتك لحفظ الموقع تلقائياً. لا نشارك بيانات الأجهزة مع أطراف خارجية." },
        { t: "صور الركن", d: "تُخزَّن الصور على جهازك افتراضياً ويمكن مزامنتها مع حسابك في ركنتي." },
        { t: "الملاحظات الصوتية والنصية", d: "تُخزَّن بأمان مرتبطة بسجل الركن الذي تنشئه." },
        { t: "معلومات الحساب", d: "الاسم والبريد ورقم الجوال لإنشاء الحساب والتواصل بشأن الخدمة." },
        { t: "التخزين السحابي", d: "المزامنة السحابية اختيارية ومشفّرة أثناء النقل. يمكنك طلب الحذف في أي وقت." },
        { t: "التحليلات", d: "نجمع تحليلات مجهولة الهوية لتحسين ركنتي. لا نبيع البيانات الشخصية." },
        { t: "الاحتفاظ بالبيانات", d: "أنت تتحكم بسجل الركن ويمكنك حذفه أو حذف حسابك في أي وقت." },
        { t: "الوصول والتصحيح والحذف", d: "يمكنك طلب الوصول أو التصحيح أو الحذف عبر Info@raknati.com." },
        { t: "المشاركة العائلية", d: "عند تفعيل المشاركة العائلية، تُشارَك بيانات محددة مع الأعضاء المدعوين." },
        { t: "مزوّدو الخدمات الخارجيون", d: "نستخدم مزوّدين موثوقين للسحابة والاتصالات والتحليلات، وكل منهم مُلزم بحماية البيانات." },
        { t: "ضوابط الأمان", d: "نستخدم تشفيراً وضوابط وصول ومراقبة لحماية بياناتك." },
        { t: "إدارة الموافقات", d: "يمكنك سحب الموافقة على أي معالجة اختيارية في أي وقت." },
        { t: "التواصل", d: "أسئلة؟ راسلنا على Info@raknati.com." },
      ],
      disclaimer:
        "هذه الوثيقة مسودة عمل ويجب مراجعتها من قِبل مستشار قانوني إماراتي مؤهل قبل الإطلاق.",
    },
  },
};
